import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ITemplateCard } from '../../viewmodels/AppViewModels';

export const BLOG_DRAFTS_QUERY_KEY = 'blogDrafts';

export function useBlogDrafts(params: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	forUserId?: string | null;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled: params.enabled,
		queryFn: async () => {
			let urlPath: string;
			const queryParams: Record<string, string> = {};

			const targetUserId = params.forUserId;

			if (targetUserId) {
				// Use specific user endpoint with forUserId parameter
				urlPath = 'blog/drafts';
				queryParams.forUserId = targetUserId;
			} else {
				// Use all users endpoint (admin mode)
				urlPath = 'blog/drafts/all';
			}

			// Always use ImpersonationBroker when impersonationContext exists
			// The forUserId parameter works with impersonation for AllowAccountAdminForUser filter
			// When a targetUserId is provided, enhance the impersonationContext to include user info
			// so that ImpersonationBroker automatically adds impUserId parameter
			const enhancedImpersonationContext =
				targetUserId && params.impersonationContext
					? {
							...params.impersonationContext,
							user: { id: targetUserId },
						}
					: params.impersonationContext;

			const url = Api.ImpersonationBroker.composeApiUrl({
				urlPath,
				queryParams,
				impersonationContext: enhancedImpersonationContext,
			});

			const result = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IBlogPost>>(url, 'GET');

			// Transform blog posts to template cards format
			const posts = result.values || [];
			return posts.map(
				(post): ITemplateCard => ({
					id: post.id,
					isDraft: true,
					name: post.title || 'Untitled Blog Post',
					summary: post.title,
					content: post.content,
					schedule: null,
					associatedTemplates: null,
					attachments: post.mainImage ? [post.mainImage] : undefined,
					creator:
						post.author && post.author.id
							? {
									id: post.author.id,
									firstName: post.author.firstName,
									lastName: post.author.lastName,
									email: post.author.email,
								}
							: null,
				})
			);
		},
		queryKey: [BLOG_DRAFTS_QUERY_KEY, params.forUserId, params.impersonationContext],
		refetchOnWindowFocus: false,
	});
}
